import React from 'react';
import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Handle, NodeProps, Position } from 'reactflow';
import { useTranslation } from 'react-i18next';

type AssistantNodeData = {
  label: string;
  isDefault?: boolean;
  isEndCallEnabled?: boolean;
  isRemoved?: boolean;
};

const routingHandleStyle: React.CSSProperties = {
  width: 1,
  height: 1,
  opacity: 0,
  border: 'none',
  background: 'transparent',
  pointerEvents: 'none',
};

export const AssistantNode: React.FC<NodeProps<AssistantNodeData>> = ({ data, selected }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const visibleHandleStyle: React.CSSProperties = {
    width: 10,
    height: 10,
    border: `2px solid ${theme.palette.extendedGrey.darkest ?? theme.palette.grey[900]}`,
    background: theme.palette.canvas.handle,
  };
  return (
    <Box
      data-testid="handoff-agent-node"
      data-agent-name={data.label}
      data-agent-removed={data.isRemoved ? 'true' : undefined}
      sx={{
        width: 180,
        minHeight: 64,
        borderRadius: 3,
        border: (theme) =>
          data.isRemoved
            ? `1px dashed ${theme.palette.error.main}`
            : `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => theme.palette.background.paper,
        p: 1.25,
        boxShadow: (theme) =>
          selected
            ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.45)}, ${theme.shadows[2]}`
            : theme.shadows[1],
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
      }}>
      <Handle type="target" position={Position.Left} id="t-left" style={visibleHandleStyle} />
      <Handle
        type="source"
        position={Position.Left}
        id="s-left"
        style={{ ...routingHandleStyle, top: '65%' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="t-right"
        style={{ ...routingHandleStyle, top: '35%' }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="s-top"
        style={{ ...routingHandleStyle, left: '65%' }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="t-top"
        style={{ ...routingHandleStyle, left: '35%' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="s-bottom"
        style={{ ...routingHandleStyle, left: '65%' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="t-bottom"
        style={{ ...routingHandleStyle, left: '35%' }}
      />
      <Stack spacing={0.75}>
        <Typography
          variant="subtitle2"
          color={data.isRemoved ? 'error' : undefined}
          sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {data.isRemoved ? t('campaignPage.squads.editor.removedAgent') : data.label}
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap">
          {data.isDefault && (
            <Chip
              size="small"
              label={t('campaignPage.squads.editor.canvas.startAgent')}
              sx={{
                width: 'fit-content',
                fontWeight: 600,
                borderRadius: 1,
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            />
          )}
          {data.isEndCallEnabled && (
            <Chip
              size="small"
              label={t('campaignPage.squads.editor.canvas.endCall')}
              sx={{
                width: 'fit-content',
                fontWeight: 600,
                borderRadius: 1,
                border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                color: 'error.main',
              }}
            />
          )}
        </Stack>
      </Stack>
      <Handle type="source" position={Position.Right} id="s-right" style={visibleHandleStyle} />
    </Box>
  );
};
